import { html } from "htm/preact";
import { useEffect, useState } from "preact/hooks";
import { navigate } from "../router.js";
import { cloneTemplate, createTournament, listTemplates } from "../http.js";
import { rememberTournament, setAuth } from "../storage.js";
import { Icon } from "../components/Icon.js";
import { InstallHint } from "../components/InstallHint.js";
import type { Template } from "../../../shared/types.js";
import { TERMS } from "../copy.js";

export function CreateTournament() {
  const [name, setName] = useState("");
  const [templates, setTemplates] = useState<Template[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    listTemplates()
      .then(setTemplates)
      .catch(() => {});
  }, []);

  async function fromScratch(e: Event) {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const { code, adminToken } = await createTournament(name.trim());
      setAuth(code, { token: adminToken, role: "admin", playerId: null });
      rememberTournament({ code, name: name.trim(), role: "admin" });
      navigate(`/t/${code}/setup`);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function fromTemplate(id: string, templateName: string) {
    setBusy(true);
    setError(null);
    try {
      const { code, adminToken } = await cloneTemplate(id);
      setAuth(code, { token: adminToken, role: "admin", playerId: null });
      rememberTournament({ code, name: templateName, role: "admin" });
      navigate(`/t/${code}/setup`);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return html`
    <h1>${`New ${TERMS.event}`}</h1>
    <p class="field-help mt-0">
      ${`You'll get a 4-letter code to share. Everyone who joins with it scores on the same card.`}
    </p>

    <!-- Here rather than only on the home screen, because this is the moment it costs
         something: the game about to be created belongs to whichever copy of the app
         creates it. Never blocks — it sits above the form and can be waved off. -->
    <${InstallHint} scope="create" />

    <form class="stack" onSubmit=${fromScratch}>
      <label class="field">
        <span class="field-label">Name</span>
        <input
          value=${name}
          placeholder="e.g. Saturday at Pebble Creek"
          onInput=${(e: any) => setName(e.currentTarget.value)}
        />
      </label>
      <button type="submit" disabled=${busy || !name.trim()}>
        <${Icon} name="flag" /> ${`Create ${TERMS.event}`}
      </button>
    </form>

    ${templates.length > 0 &&
    html`
      <h2>Or start from a saved setup</h2>
      <div class="tap-list">
        ${templates.map(
          (t) => html`
            <button key=${t.id} disabled=${busy} onClick=${() => fromTemplate(t.id, t.name)}>
              <${Icon} name="copy" size=${18} />
              <span class="list-main">${t.name}</span>
              <${Icon} name="right" size=${18} />
            </button>
          `,
        )}
      </div>
    `}
    ${error && html`<p class="notice notice-error mt-3"><${Icon} name="alert" size=${16} /> ${error}</p>`}
  `;
}
